import { translate } from "app/filters/i18nFilter";
import { VISIBILITY_BADGE_TRANSLATION_KEYS } from "app/pages/sales-intelligence/common-components/VisibilityBadge/visibilityBadgeTranslationKeys";
import {
    type LeadPriorityScore,
    SCORING_PRIORITIES,
} from "app/pages/sales-intelligence/domain/leads/types/lead-priority";
import {
    type IScoringRuleDto,
    type IScoringRuleExportRowDto,
    type ScoringConfigRule,
    ScoringConfigType,
} from "app/pages/sales-intelligence/domain/leads/types/scoring-config";
import type { TeamListItem } from "app/pages/sales-intelligence/domain/teams/types/team-list-item";
import { buildLeadsFilterSummary } from "app/pages/sales-intelligence/modules/leads-search-filters/utils/buildLeadsFilterChips";
import { globalStringSanitizePipeline } from "app/pages/sales-intelligence/services/text-transform/stringTransformPipeline";
import type {
    SignalByIdMap,
    UITranslatedSignal,
} from "app/pages/sales-intelligence/sub-modules/signals/types/extended-models";
import signalsI18nKeys from "app/pages/sales-intelligence/sub-modules/signals/utils/i18n";

import { LEAD_SCORE_TYPE_CONFIG } from "../configuration/score-type";
import { SCORING_EXPORT_TRANSLATION_KEYS } from "../constants/translations";

import { resolveScoringRuleTeamNames } from "./resolveScoringRuleTeamNames";

/** Multi-value separator of the approved report template — used within a cell, never between rows. */
const VALUE_SEPARATOR = "; ";

const { criterionType: CRITERION_TYPE_KEYS } = SCORING_EXPORT_TRANSLATION_KEYS;

/** The rule-level columns, repeated on every one of the rule's criterion rows. */
type RuleColumns = Omit<IScoringRuleExportRowDto, "criterionType" | "criterionName" | "criterionValue">;

type BuildScoringRulesExportRowsArgs = {
    /** The rules currently on the board — the segment the user is looking at. */
    readonly rules: ScoringConfigRule;
    readonly getRuleScope: (rule: IScoringRuleDto) => ScoringConfigType | undefined;
    /** `filterKey → translated title`, from `buildFilterTitleByKey` over the board's filters config. */
    readonly titleByKey: ReadonlyMap<string, string>;
    readonly signalById: SignalByIdMap;
    /** Empty when teams are unavailable, which leaves the Teams column blank. */
    readonly teams: readonly TeamListItem[];
};

const buildRuleColumns = (
    rule: IScoringRuleDto,
    priority: LeadPriorityScore,
    { getRuleScope, teams }: BuildScoringRulesExportRowsArgs,
): RuleColumns => {
    const isShared = getRuleScope(rule) === ScoringConfigType.Account;

    return {
        priority: translate(LEAD_SCORE_TYPE_CONFIG[priority].labelKey),
        businessContext: rule.businessContext?.translation ?? "",
        actionItem: rule.businessContext?.actionItem ?? "",
        teams: isShared ? resolveScoringRuleTeamNames(rule.teamIds, teams).join(VALUE_SEPARATOR) : "",
        visibility: translate(
            isShared ? VISIBILITY_BADGE_TRANSLATION_KEYS.shared : VISIBILITY_BADGE_TRANSLATION_KEYS.private,
        ),
    };
};

/**
 * One row per selected signal, named by its type — mirrors how the card lists them. Signal names carry the
 * markup the board renders as rich text, so they are flattened the same way plain-text views flatten them.
 */
const buildSignalRows = (
    rule: IScoringRuleDto,
    columns: RuleColumns,
    signalById: SignalByIdMap,
): IScoringRuleExportRowDto[] =>
    rule.signalIds
        .map((signalId) => signalById.get(signalId))
        .filter((signal): signal is UITranslatedSignal => signal !== undefined)
        .map((signal) => ({
            ...columns,
            criterionType: translate(CRITERION_TYPE_KEYS.signal),
            criterionName: translate(signalsI18nKeys.signalType(signal.signalType)),
            criterionValue: globalStringSanitizePipeline.apply(signal.translatedName),
        }));

/** One row per set filter, with the same names and values the rule card renders. */
const buildFilterRows = (
    rule: IScoringRuleDto,
    columns: RuleColumns,
    titleByKey: ReadonlyMap<string, string>,
): IScoringRuleExportRowDto[] =>
    buildLeadsFilterSummary(rule.filters, titleByKey, undefined, VALUE_SEPARATOR).map(({ name, value }) => ({
        ...columns,
        criterionType: translate(CRITERION_TYPE_KEYS.filter),
        criterionName: name,
        criterionValue: value,
    }));

/**
 * Flattens the scoring board into spreadsheet rows: one row per criterion, rules grouped together and
 * ordered High → Medium → Low. Row order is the file order — the backend neither sorts nor groups.
 */
export const buildScoringRulesExportRows = (
    args: BuildScoringRulesExportRowsArgs,
): readonly IScoringRuleExportRowDto[] => {
    const { rules, signalById, titleByKey } = args;

    return SCORING_PRIORITIES.flatMap((priority) =>
        (rules[priority] ?? []).flatMap((rule) => {
            const columns = buildRuleColumns(rule, priority, args);

            return [...buildSignalRows(rule, columns, signalById), ...buildFilterRows(rule, columns, titleByKey)];
        }),
    );
};
